import React, { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AEO_HUBS, articlesForIcp, findArticle } from './aeoArticlesData';
import { aeoCoverForSlug } from './aeoCovers';
import useDocumentHead from './useDocumentHead';
import { useReveal } from '../common/landingHooks';

/**
 * AEO article page — hub-and-spoke spoke. One focused question per URL at /[icpSlug]/[slug].
 * Renders H1 question, direct-answer body, People-also-ask, FAQ section (required for FAQPage
 * schema), contextual hub link CTA, and sibling-article links for the cluster.
 */
const AeoArticlePage = ({ icpSlug, slugOverride }) => {
    const params = useParams();
    const articleSlug = slugOverride || params['articleSlug'];
    const article = findArticle(icpSlug, articleSlug);
    const hub = AEO_HUBS[icpSlug];
    const rootRef = useRef(null);
    useReveal(rootRef);

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [icpSlug, articleSlug]);

    useDocumentHead({
        title: article ? article['title'] : 'Article not found',
        description: article ? article['metaDescription'] : '',
        canonicalPath: article ? `/${article['icpSlug']}/${article['slug']}/` : undefined,
        schema: article ? article['schema'] : [],
    });

    if (!article || !hub) return (
        <div className="lp-page"><section className="lp-aeo-article"><div className="lp-aeo-inner"><h1 className="lp-h1">Article not found</h1><p className="lp-lead">This article does not exist. Browse our <Link to="/resources">resources</Link> instead.</p></div></section></div>
    );

    const siblings = articlesForIcp(icpSlug).filter(a => a['slug'] !== article['slug']);
    // Rough read time from the body text (same 200wpm heuristic the blog pages use)
    const readMins = Math.max(1, Math.round(article['bodyHtml'].replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length / 200));

    return (
        <div className="lp-page" ref={rootRef}>
            {/* Same hero banner treatment as the blog detail page — cover as background, back link, tags, title, meta */}
            <section className="lp-article-hero" style={{ backgroundImage: `url(${aeoCoverForSlug(article['slug'], article['image'])})` }}>
                <div className="overlay" />
                <div className="content">
                    <Link to="/resources" className="back"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>Back to Resources</Link>

                    <div className="tags"><span>{article['category']}</span></div>

                    <h1>{article['title']}</h1>

                    <div className="meta">
                        <span className="author">
                            <span className="avatar"><svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg></span>
                            Diligentsia
                        </span>
                        <span className="sep">·</span>
                        <span>Last updated {article['lastUpdated']}</span>
                        <span className="sep">·</span>
                        <span>{readMins} min read</span>
                    </div>
                </div>
            </section>

            <article className="lp-aeo-article lp-aeo-article--with-hero">
                <div className="lp-aeo-inner">
                    <nav className="lp-aeo-breadcrumb" aria-label="Breadcrumb">
                        <Link to="/">Home</Link><span>/</span>
                        <Link to={hub['hubPath']}>{hub['name']}</Link><span>/</span>
                        <span className="lp-aeo-crumb-current">{article['title']}</span>
                    </nav>

                    <p className="lp-aeo-meta"><em>{article['metaDescription']}</em></p>

                    <div className="lp-aeo-body" dangerouslySetInnerHTML={{ __html: article['bodyHtml'] }} />

                    {article['peopleAlsoAsk'] && article['peopleAlsoAsk'].length > 0 && (
                        <section className="lp-aeo-faq">
                            <h2>People also ask</h2>
                            {article['peopleAlsoAsk'].map((qa, i) => (
                                <div className="lp-aeo-faq-item" key={`paa-${i}`}>
                                    <h3>{qa['q']}</h3>
                                    <p>{qa['a']}</p>
                                </div>
                            ))}
                        </section>
                    )}

                    {/* Not every source article ships an FAQ block — render the heading only when there are questions,
                        otherwise the page shows an empty "Frequently asked questions" section. */}
                    {article['faqs'] && article['faqs'].length > 0 && (
                        <section className="lp-aeo-faq">
                            <h2>Frequently asked questions</h2>
                            {article['faqs'].map((qa, i) => (
                                <div className="lp-aeo-faq-item" key={`faq-${i}`}>
                                    <h3>{qa['q']}</h3>
                                    <p>{qa['a']}</p>
                                </div>
                            ))}
                        </section>
                    )}

                    <section className="lp-aeo-cta">
                        <h2>Get investment-ready with Diligentsia</h2>
                        <p>
                            Build a certified, evidence-linked Diligence Pack with AI assistance — from £1,000/year.
                            Learn more about <Link to={hub['hubPath']}>{hub['anchorText']}</Link>.
                        </p>
                        <Link className="lp-btn lp-btn--primary" to="/register">Get started free</Link>
                    </section>

                    {siblings.length > 0 && (
                        <section className="lp-aeo-related">
                            <h2>Related articles</h2>
                            <ul>
                                {siblings.map(s => (
                                    <li key={s['slug']}>
                                        <Link to={`/${s['icpSlug']}/${s['slug']}`}>{s['title']}</Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <p className="lp-aeo-published">Published by Diligentsia · diligentsia.co.uk · Last updated {article['lastUpdated']}</p>
                </div>
            </article>
        </div>
    );
};

export default AeoArticlePage;
